import { Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { InsertCommand } from '@src/suite/common/store/story/models/story.models';
import { ML2HtmlUtils, ProductionCommandFieldsLabelKeys, ProductionFieldName } from '@src/suite/common/utils/ml-2-html-utils';
import { getSecondsFromTimeSpan, getTimeSpanWithHoursFromSeconds } from '@src/suite/common/utils/timing-format';
import { substituteWhitespace } from '@src/suite/common/utils/util';

import { ComboBoxComponent } from '../../../controls/combo-box/combo-box';
import { DurationSecondsInputFieldComponent } from '../../../controls/duration-seconds-input-field/duration-seconds-input-field';
import { TextFieldComponent } from '../../../controls/text-field/text-field';
import { ClickStopPropagationDirective } from '../../../directives/stop-propagation';

@Component({
    selector: 'st-insert-take-command-dialog',
    imports: [ClickStopPropagationDirective, ComboBoxComponent, DurationSecondsInputFieldComponent, LetDirective, MatRadioButton, MatRadioGroup, TextFieldComponent],
    templateUrl: './insert-take-command-dialog.html',
    styleUrl: './insert-take-command-dialog.scss',
    encapsulation: ViewEncapsulation.None,
    host: { class: 'st-insert-take-command-dialog' },
})
export class InsertTakeCommandDialogComponent {
    // injectables
    store = inject(Store);

    // store signals
    languageSettings = this.store.selectSignal(selectI18N);

    // inputs
    readonly command = input<InsertCommand>();

    // outputs
    readonly commandChange = output<InsertCommand>();
    readonly enterEmitter = output<InsertCommand>();

    // computed
    readonly cmdOption = computed(() => {
        return substituteWhitespace(this.command()[ProductionFieldName.OPTION]);
    });

    readonly getCurrentCommandValue = computed(() => {
        const cmd = this.productionCommandsObjectOptions.find(option => option.title === this.cmdOption());
        return {
            id: cmd?.id || this.command()[ProductionFieldName.OPTION],
            title: cmd?.title || this.command()[ProductionFieldName.OPTION_DESCRIPTION],
        };
    });

    // constants
    ProductionFieldName = ProductionFieldName;
    ProductionCommandFieldsLabelKeys = ProductionCommandFieldsLabelKeys;
    productionCommandsObjectOptions = ML2HtmlUtils.productionCommandsObjectOptions();

    // methods
    public onCommandsObjectChange(event: any, fieldName: ProductionFieldName) {
        if (this.command()) {
            const updatedCommand = { ...this.command() };
            updatedCommand[fieldName] = fieldName === ProductionFieldName.OPTION ? event.id : event;
            if (
                (fieldName === ProductionFieldName.AT || fieldName === ProductionFieldName.TO) &&
                updatedCommand[ProductionFieldName.TO] &&
                updatedCommand[ProductionFieldName.AT] < updatedCommand[ProductionFieldName.TO]
            ) {
                const timeValue = getSecondsFromTimeSpan(updatedCommand[ProductionFieldName.TO]) - getSecondsFromTimeSpan(updatedCommand[ProductionFieldName.AT]);
                updatedCommand[ProductionFieldName.TIME_VALUE] = getTimeSpanWithHoursFromSeconds(timeValue);
            }
            this.commandChange.emit(updatedCommand);
        }
    }

    public saveCommand(event: any, fieldName: ProductionFieldName) {
        this.onCommandsObjectChange(event, fieldName);
        this.enterEmitter.emit(this.command());
    }

    public onKeyup(event: KeyboardEvent, fieldName: ProductionFieldName) {
        if (!(event?.target as unknown as { value: string })?.value) {
            return;
        }
        if (event.keyCode === 13 || event.key === 'Enter') {
            this.saveCommand((event?.target as unknown as { value: string }).value, fieldName);
        }
    }
}
